"use client";

import PropTypes from "prop-types";
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import SignatureCanvas from "react-signature-canvas";

const DEFAULT_HEIGHT = 200;
const DEFAULT_WIDTH = 600;

const SignatureField = ({
  label,
  name,
  register,
  setValue,
  clearErrors,
  trigger,
  errors,
  existingValue,
  metadataFieldName,
  onSignatureRef,
  labelPosition = "top",
  fieldHeight = "medium",
  fieldWidth = undefined,
  fieldAlignment = "left",
  labelColor = "text-gray-700",
}) => {
  const sigPadRef = useRef(null);
  const containerRef = useRef(null);
  const skipNextExistingValue = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(DEFAULT_WIDTH);
  const [isSignatureReady, setIsSignatureReady] = useState(false);
  const [ipAddress, setIpAddress] = useState(null);

  const registerField = useMemo(() => register(name), [register, name]);
  const registerMetadataField = useMemo(
    () => register(metadataFieldName),
    [register, metadataFieldName]
  );

  const updateCanvasWidth = useCallback(() => {
    if (!containerRef.current) {
      return;
    }
    const width = containerRef.current.offsetWidth;
    if (width) {
      setCanvasWidth(width);
    }
  }, []);

  useEffect(() => {
    updateCanvasWidth();
    window.addEventListener("resize", updateCanvasWidth);
    return () => window.removeEventListener("resize", updateCanvasWidth);
  }, [updateCanvasWidth]);

  useEffect(() => {
    let isMounted = true;
    const fetchIp = async () => {
      try {
        const response = await fetch("https://api.ipify.org?format=json");
        if (!response.ok) {
          throw new Error("Failed to fetch IP");
        }
        const data = await response.json();
        if (isMounted) {
          setIpAddress(data?.ip || null);
        }
      } catch {
        if (isMounted) {
          setIpAddress(null);
        }
      }
    };
    fetchIp();
    return () => {
      isMounted = false;
    };
  }, []);

  const loadExistingSignature = useCallback(
    (value) => {
      if (!sigPadRef.current || !value) {
        return;
      }
      try {
        sigPadRef.current.fromDataURL(value, { ratio: 1.0 });
        setHasSignature(true);
      } catch {
        /* ignore invalid signature data URL */
      }
    },
    []
  );

  useEffect(() => {
    if (!isSignatureReady) {
      return;
    }
    if (!existingValue) {
      sigPadRef.current?.clear();
      setHasSignature(false);
      return;
    }
    if (skipNextExistingValue.current) {
      skipNextExistingValue.current = false;
      return;
    }
    loadExistingSignature(existingValue);
  }, [existingValue, loadExistingSignature, isSignatureReady]);

  const buildMetadata = useCallback(() => ({
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screen: {
      width: window.screen?.width,
      height: window.screen?.height,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer: document.referrer,
    currentUrl: window.location.href,
    ipAddress,
    signedAt: new Date().toISOString(),
  }), [ipAddress]);

  const persistSignature = useCallback(() => {
    if (!sigPadRef.current) {
      return;
    }
    
    // Check if isEmpty method exists
    if (typeof sigPadRef.current.isEmpty !== 'function') {
            return;
    }
    
    if (sigPadRef.current.isEmpty()) {
      setHasSignature(false);
      skipNextExistingValue.current = true;
      setValue(name, "", { shouldValidate: true, shouldDirty: true });
      setValue(metadataFieldName, "", { shouldDirty: true });
      return;
    }
    
    // Check if getTrimmedCanvas method exists
    if (typeof sigPadRef.current.getTrimmedCanvas !== 'function') {
            return;
    }
    
    try {
      const trimmedCanvas = sigPadRef.current.getTrimmedCanvas();
      if (!trimmedCanvas || typeof trimmedCanvas.toDataURL !== 'function') {
                return;
      }
      
      const dataUrl = trimmedCanvas.toDataURL("image/png");
      skipNextExistingValue.current = true;
      setValue(name, dataUrl, { shouldValidate: true, shouldDirty: true });
      const metadata = JSON.stringify(buildMetadata());
      setValue(metadataFieldName, metadata, {
        shouldValidate: false,
        shouldDirty: true,
      });
      setHasSignature(true);
      clearErrors?.(name);
      // Trigger validation to ensure the field is validated with the new value
      trigger?.(name);
    } catch {
      /* persist failed */
    }
  }, [name, metadataFieldName, setValue, clearErrors, buildMetadata, trigger]);

  // Expose persistSignature method to parent component
  useEffect(() => {
    if (onSignatureRef) {
      onSignatureRef({
        persist: persistSignature,
        isEmpty: () => {
          if (!sigPadRef.current) {
            return true;
          }
          try {
            // Try multiple ways to check if empty
            if (typeof sigPadRef.current.isEmpty === 'function') {
              return sigPadRef.current.isEmpty();
            }
            // Fallback: check if canvas has any content
            if (sigPadRef.current.canvas) {
              const ctx = sigPadRef.current.canvas.getContext('2d');
              const imageData = ctx.getImageData(0, 0, sigPadRef.current.canvas.width, sigPadRef.current.canvas.height);
              const hasContent = imageData.data.some((channel, index) => index % 4 !== 3 && channel !== 0);
              return !hasContent;
            }
            return true;
          } catch {
            return true;
          }
        },
        getDataUrl: () => {
          if (!sigPadRef.current) {
            return null;
          }
          try {
            // Try getTrimmedCanvas first (preferred method)
            if (typeof sigPadRef.current.getTrimmedCanvas === 'function') {
              try {
                const trimmedCanvas = sigPadRef.current.getTrimmedCanvas();
                if (trimmedCanvas && typeof trimmedCanvas.toDataURL === 'function') {
                  return trimmedCanvas.toDataURL("image/png");
                }
              } catch {
                // Fall through to alternative method
              }
            }
            
            // Fallback: use canvas directly
            if (sigPadRef.current.canvas) {
              return sigPadRef.current.canvas.toDataURL("image/png");
            }
            
            // Last resort: try toDataURL directly on the ref
            if (typeof sigPadRef.current.toDataURL === 'function') {
              return sigPadRef.current.toDataURL("image/png");
            }
            
            return null;
          } catch {
            return null;
          }
        },
      });
    }
  }, [onSignatureRef, persistSignature, isSignatureReady]);

  const handleBegin = useCallback(() => {
    // Clear error immediately when user starts signing
    clearErrors?.(name);
  }, [name, clearErrors]);

  const handleClear = () => {
    sigPadRef.current?.clear();
    skipNextExistingValue.current = true;
    setValue(name, "", { shouldValidate: true, shouldDirty: true });
    setValue(metadataFieldName, "", { shouldDirty: true });
    setHasSignature(false);
  };

  // Get width classes based on fieldWidth and fieldAlignment props
  // Only apply width restrictions if fieldWidth is explicitly provided
  // If not provided (form builder), use full width without restrictions
  const getWidthClasses = () => {
    if (!fieldWidth || fieldWidth === undefined) {
      return "w-full"; // Full width for form builder
    }
    
    // Get alignment margin class
    const alignmentMargin = fieldAlignment === "center" ? "mx-auto" : fieldAlignment === "right" ? "ml-auto" : "";
    
    switch (fieldWidth) {
      case "small":
        return `w-full max-w-sm ${alignmentMargin}`;
      case "large":
        return "w-full"; // Full width (matches container width)
      case "medium":
      default:
        return `w-full max-w-xl ${alignmentMargin}`;
    }
  };

  // Get height for signature canvas based on fieldHeight prop
  const getSignatureHeight = () => {
    switch (fieldHeight) {
      case "small":
        return 150;
      case "large":
        return 250;
      case "medium":
      default:
        return DEFAULT_HEIGHT;
    }
  };

  const widthClasses = getWidthClasses();
  const signatureHeight = getSignatureHeight();

  // Determine label rendering based on labelPosition
  const shouldShowLabel = label && labelPosition !== "placeholder";
  const shouldShowLabelTop = shouldShowLabel && labelPosition === "top";
  const shouldShowLabelLeft = shouldShowLabel && labelPosition === "left";
  const labelStyle = labelColor ? { color: labelColor } : { color: '#374151' };

  // Get label classes based on labelPosition
  const getLabelClasses = () => {
    if (labelPosition === "left") {
      return "block text-gray-700 text-sm sm:text-base whitespace-nowrap min-w-[120px] mb-0";
    }
    if (labelPosition === "placeholder") {
      return "hidden";
    }
    return "block text-gray-700 text-sm sm:text-base mb-2 sm:mb-2.5";
  };

  return (
    <div className={`relative ${widthClasses}`}>
      {shouldShowLabelTop && (
        <label className={getLabelClasses()} style={labelStyle} htmlFor={name}>
          {label}
        </label>
      )}
      <div className={shouldShowLabelLeft ? "flex items-start gap-3" : ""}>
        {shouldShowLabelLeft && (
          <label className={getLabelClasses()} style={labelStyle} htmlFor={name}>
            {label}
          </label>
        )}
        <div className={`${shouldShowLabelLeft ? "flex-1 min-w-0" : ""}`}>
          <div
            ref={containerRef}
            className="border border-gray-300 rounded-md bg-white"
            style={{ minHeight: signatureHeight }}
          >
            <SignatureCanvas
              ref={(ref) => {
                sigPadRef.current = ref;
                setIsSignatureReady(Boolean(ref));
              }}
              canvasProps={{
                className: "w-full h-full rounded-md bg-white",
                width: canvasWidth,
                height: signatureHeight,
              }}
              penColor="#111827"
              backgroundColor="#FFFFFF"
              minDistance={0.5}
              throttle={16}
              clearOnResize={false}
              onBegin={handleBegin}
              onEnd={persistSignature}
            />
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-gray-500">
              {hasSignature ? "Signature captured" : "Sign inside the box above"}
            </span>
            <button
              type="button"
              className="text-sm text-primary font-medium hover:underline"
              onClick={handleClear}
            >
              Clear
            </button>
          </div>
          <input type="hidden" {...registerField} />
          <input type="hidden" {...registerMetadataField} />
          {errors?.[name] && (
            <p className={`text-red-600 text-sm mt-1 ${shouldShowLabelLeft ? "ml-0" : ""}`}>
              {errors[name]?.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignatureField;

SignatureField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  register: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  clearErrors: PropTypes.func,
  trigger: PropTypes.func,
  errors: PropTypes.objectOf(
    PropTypes.shape({
      message: PropTypes.string,
    })
  ),
  existingValue: PropTypes.string,
  metadataFieldName: PropTypes.string.isRequired,
  onSignatureRef: PropTypes.func,
  labelPosition: PropTypes.oneOf(["top", "left", "placeholder"]),
  fieldHeight: PropTypes.oneOf(["small", "medium", "large"]),
  fieldWidth: PropTypes.oneOf(["small", "medium", "large", undefined]),
  fieldAlignment: PropTypes.oneOf(["left", "center", "right"]),
};

SignatureField.defaultProps = {
  clearErrors: undefined,
  errors: undefined,
  existingValue: "",
  labelPosition: "top",
  fieldHeight: "medium",
  fieldWidth: undefined,
  fieldAlignment: "left",
};

